/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Power4, Timeline, TimelineLite, TweenLite} from "gsap";
import {TombstoneGameConfig} from "../../TombstoneGameConfig";
import {TombstoneSoundConfig} from "../../TombstoneSoundConfig";
import {CoinsFountain} from "./CoinsFountain";

export class TombstoneBigWinView extends BaseView {

    private readonly _f:number = 0.8;
    private readonly _holePositions:[number, number][][] = [
        [[-178 * this._f, -121 * this._f], [45 * this._f, -118 * this._f], [83 * this._f, -82 * this._f]],
        [[-130 * this._f, -110 * this._f], [-37 * this._f, 36 * this._f], [-6 * this._f, -36 * this._f], [125 * this._f, -135 * this._f]],
        [[-144 * this._f, -131 * this._f], [-14 * this._f, 14 * this._f], [-7 * this._f, -34 * this._f], [43 * this._f, -98 * this._f], [124 * this._f, -131 * this._f]]
    ];

    private _layer:ScalableLayer;
    private _dimmer:PIXI.Graphics;
    private _rays:TimelineSprite;
    private _textContainer:PIXI.Container;
    private _texts:PIXI.Sprite[];
    private _shootContainer:PIXI.Container;
    private _coinsFountain:CoinsFountain;
    private _isShowing:boolean = false;

    constructor() {
        super();

        this._layer = StageManager.getLayer(TombstoneGameConfig.instance.LAYERS.BIG_WIN.name);
        this._coinsFountain = new CoinsFountain();
    }

    protected initAnimations():void {
        this._dimmer = new PIXI.Graphics();
        this._dimmer.blendMode = PIXI.BLEND_MODES.MULTIPLY;
        this._dimmer.alpha = 0;
        this._layer.addChild(this._dimmer);

        this._rays = new TimelineSprite(GameResources.getTextures("bigWinRays"));
        this._rays.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._rays.anchor.set(0.5, 0.5);
        this._rays.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.3);
        this._rays.scale.set(0.1, 0.1);
        this._rays.alpha = 0;
        this._layer.addChild(this._rays);

        this._textContainer = new PIXI.Container();
        this._textContainer.scale.set(0.9, 0.9);
        this._textContainer.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.3);
        this._layer.addChild(this._textContainer);

        this._texts = [];
        for(let i:number = 0; i < 3; i++) {
            const text:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(`bigWinText${i}`)[0]);
            text.anchor.set(0.5, 0.5);
            text.scale.set(0.1, 0.1);
            text.alpha = 0;
            this._textContainer.addChild(text);
            this._texts.push(text);
        }

        this._shootContainer = new PIXI.Container();
        this._shootContainer.alpha = 0;
        this._textContainer.addChild(this._shootContainer);
    }

    protected onResize(resizeData:IResizeData):void {
        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        const screenWidth:number = Math.ceil(resizeData.width / this._layer.scale.x);
        const screenHeight:number = Math.ceil(resizeData.height / this._layer.scale.y);
        const leftOffset:number = Math.floor(-this._layer.x / this._layer.scale.x);
        const topOffset:number = Math.floor(-this._layer.y / this._layer.scale.y);

        this._dimmer.clear();
        this._dimmer.beginFill(0x2C0013, 0.9);
        this._dimmer.drawRect(leftOffset, topOffset, screenWidth, screenHeight);

        this._isResizeDirty = false;
    }

    public getBigWinLevelAnimation(winRatioConfig:IWinRatioConfig, level:number):Timeline {
        this._isShowing = true;
        if(this._isResizeDirty) {
            this.onResize(this._resizeData);
        }

        const raysScaleTimeline:TimelineLite = new TimelineLite();
        raysScaleTimeline.add(TweenLite.to(this._rays.scale, 0.3, {x : 2.8, y : 2.8}));
        raysScaleTimeline.add(TweenLite.to(this._rays.scale, 0.2, {x : 2, y : 2}));

        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._rays.playLoop();
                this._coinsFountain.startFountain(level);
            },
            TweenLite.to(this._dimmer, 0.1, {alpha : 1}),
            TweenLite.to(this._rays, 0.1, {alpha : 1}),
            raysScaleTimeline,
            TweenLite.to(this._texts[level], 0.5, {alpha : 1}),
            TweenLite.fromTo(this._texts[level].scale, 0.5, {x : 1.7, y : 1.7}, {x : 1, y : 1, ease : Power4.easeIn}),
            this.getLevelShootAnimation(level, false),
            TweenLite.to(this, winRatioConfig.duration, {alpha : 1})
        ]);

        if(level > 0) {
            timeline.add([
                TweenLite.to(this._texts[level - 1], 0.4, {alpha : 0}),
            ], 0);
        }

        return timeline;
    }

    private getLevelShootAnimation(level:number, isEnding:boolean):TimelineLite {
        const timeline:TimelineLite = new TimelineLite();
        if(!isEnding) {
            timeline.add(TweenLite.to(this._shootContainer, 0.4, {
                alpha : 0,
                onComplete : () => this.clearAllShoots()
            }));
        }

        // the animation is already work well, so I will not change it to the ShootingAnimation unless we do some change later.
        this._holePositions[level].forEach((position:[number, number], index:number) => {
            const hole:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("bulletHole")[0]);
            hole.anchor.set(0.5, 0.5);
            // hole.rotation = Math.random() * Math.PI * 2;
            hole.position.set(position[0], position[1]);
            hole.visible = false;
            const smoke:TimelineSprite = new TimelineSprite(GameResources.getTextures("holeSmoke"));
            smoke.blendMode = PIXI.BLEND_MODES.SCREEN;
            smoke.anchor.set(0.5, 1);
            smoke.scale.set(2, 2);
            smoke.position.set(position[0], position[1] + Math.round(hole.height / 2));
            smoke.hide();
            const spark:TimelineSprite = new TimelineSprite(GameResources.getTextures("sparkSmoke"));
            spark.blendMode = PIXI.BLEND_MODES.SCREEN;
            spark.anchor.set(0.5, 0.5);
            spark.position.set(position[0], position[1]);
            spark.hide();

            const shootTimeline:TimelineLite = new TimelineLite();
            shootTimeline.add([
                () => {
                    this._shootContainer.alpha = 1;
                    this._shootContainer.addChild(hole);
                    this._shootContainer.addChild(smoke);
                    this._shootContainer.addChild(spark);
                    if(!isEnding) {
                        SlotGame.sound.playEffect(TombstoneSoundConfig.getPistolSoundName());
                    }
                },
                spark.getAnimationAutoShowHide(),
                smoke.getAnimationAutoShowHide()
            ]);
            shootTimeline.add(() => hole.visible = true, 0.05);

            timeline.add(shootTimeline, 0.5 + 0.5 * index);
        });

        return timeline;
    }

    private clearAllShoots():void {
        if(this._shootContainer.children.length > 0) {
            this._shootContainer.removeChildren();
        }
    }

    public playEndingAnimation(endLevel:number):void {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._texts[endLevel].scale.set(1, 1);
                this._coinsFountain.stopFountain();
            },
            TweenLite.to(this._dimmer, 0.1, {alpha : 0}),
            TweenLite.to(this._rays.scale, 0.2, {x : 0.1, y : 0.1, delay : 0.2}),
            TweenLite.to(this._rays, 0.1, {alpha : 0, delay : 0.3}),
            TweenLite.fromTo(this._texts[endLevel], 0.2, {alpha : 1}, {alpha : 0, delay : 0.8}),
            TweenLite.fromTo(this._shootContainer, 0.2, {alpha : 1}, {alpha : 0, delay : 0.8})
        ]);
        if(endLevel > 0) {
            for(let i:number = 0; i < endLevel; i++) {
                timeline.add(TweenLite.to(this._texts[i], 0.3, {alpha : 0}), 0);
            }
        }
        timeline.add(() => {
            this.clearAllShoots();
            this._rays.stopLoop();
            this._isShowing = false;
        });

        timeline.play();

        this.clearAllShoots();
        const shootTimeline:TimelineLite = this.getLevelShootAnimation(endLevel, true);
        shootTimeline.progress(1);
    }
}